import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  resetKey: string;
  onReset: () => void;
}

interface State {
  hasError: boolean;
}

/**
 * Keeps a single malformed conversation from unmounting the whole SPA.
 * The fallback intentionally contains no error text or message identifiers.
 */
export default class ChatContentErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // React already reports the exception to the console.  Do not duplicate
    // it with route/user/message metadata in production logs.
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  private retry = (): void => {
    this.props.onReset();
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex flex-1 items-center justify-center bg-surface-950 p-6 text-center">
        <div className="max-w-sm rounded-2xl border border-surface-800 bg-surface-900 p-6 shadow-xl">
          <AlertTriangle className="mx-auto mb-3 text-amber-400" size={32} />
          <h2 className="text-lg font-semibold text-surface-100">Не удалось открыть диалог</h2>
          <p className="mt-2 text-sm text-surface-400">
            Локальный кэш диалога будет очищен. Сообщения на сервере не удаляются.
          </p>
          <button
            type="button"
            onClick={this.retry}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500"
          >
            <RefreshCw size={16} />
            Повторить
          </button>
        </div>
      </div>
    );
  }
}
